import { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { useDebounce } from "$utils/customHooks";
import SearchSelect from "./SearchSelect";
import { appState } from "$reducers";
import { Company, CompanyType, DeliveryType } from "$types";
import { GenericSelectProps } from "./GenericSelect";

type Props = {
  onChange: (companyId, company) => void;
  value: any;
  className?: string;
  error?: boolean | FieldError | Merge<FieldError, FieldErrorsImpl<any>> | any;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  activeCompaniesOnly?: boolean;
  supplierId?: string;
  deliveryTypeFilter?: DeliveryType;
  label?: string;
  placeholder?: string;
  companyOptions?: Company[];
  companyTypes?: CompanyType[];
  ignoreSideBarCompanyFilter?: boolean;
  minCharsToTrigerSearch?: number;
  valueKey?: string;
  rowsPerPage?: number;
  onDataFetched?: (options: any[]) => void;
  // Catalogue only: reopening the field shows every company, not just the one
  // already selected. Other screens keep the previous dropdown behavior.
  expandOptionsWhenSelected?: boolean;
};

const getCompanyLabel = (option) =>
  `${option?.name}${option?.code ? ` - [${option?.code}]` : ""}`;

const CompanySelect = ({
  disabled,
  className,
  activeCompaniesOnly = true,
  supplierId,
  label = "Entreprise",
  placeholder = "Sélectionnez une entreprise",
  multiple = false,
  value,
  deliveryTypeFilter,
  companyOptions,
  minCharsToTrigerSearch = 2,
  companyTypes,
  ignoreSideBarCompanyFilter = false,
  valueKey = "_id",
  rowsPerPage = 20,
  onDataFetched,
  expandOptionsWhenSelected = false,
  error,
  ...props
}: Props & Partial<GenericSelectProps>) => {
  const { filter: sidebarCompanyFilter } = useSelector(appState);

  // Input text value is debounced to prevent too many calls
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);

  // Selected option(s) stored locally
  const [localOptions, setLocalOptions] = useState([]);

  // Track if we're updating from the parent component
  const [internalUpdate, setInternalUpdate] = useState(false);

  // If value changes externally (from parent), update the input value to match
  useEffect(() => {
    if (!internalUpdate && value && localOptions.length > 0) {
      const selectedOption = multiple
        ? localOptions.filter((opt) => (value || []).includes(opt[valueKey]))
        : localOptions.find((opt) => opt[valueKey] === value);

      if (selectedOption && !multiple) {
        setInputValue(getCompanyLabel(selectedOption));
      }
    }
  }, [value, localOptions, multiple, valueKey, internalUpdate]);

  const filters = useMemo(() => {
    const safeLocalOptions = Array.isArray(localOptions) ? localOptions : [];

    const nextFilters = {
      ...(!!debouncedInputValue && { name: debouncedInputValue }),
      isActive: activeCompaniesOnly,
      $inCompanyTypes: companyTypes,
      ...(supplierId ? { supplierOf: supplierId } : {}),
      page: 1,
      ...(deliveryTypeFilter ? { deliveryType: deliveryTypeFilter } : {}),
      rowsPerPage,
    };

    // Check if search matches a selected option to avoid searching for the label itself
    let isSearchStringEqualsAnOption = false;

    if (multiple && Array.isArray(value)) {
      isSearchStringEqualsAnOption = (value || []).some((id) => {
        const opt = safeLocalOptions.find((opt) => opt[valueKey] === id);
        return opt && getCompanyLabel(opt) === debouncedInputValue;
      });
    } else if (value && safeLocalOptions.length > 0) {
      const foundOpt = safeLocalOptions.find((opt) => opt[valueKey] === value);
      isSearchStringEqualsAnOption =
        foundOpt &&
        (getCompanyLabel(foundOpt) === inputValue ||
          foundOpt.name === debouncedInputValue);
    }

    // Only prevent the name filter if we're displaying a selected option's full label
    if (
      !multiple &&
      isSearchStringEqualsAnOption &&
      inputValue &&
      inputValue.includes(" - [")
    ) {
      nextFilters.name = undefined;
    }

    // Catalogue: a company without a code has a label equal to its name.
    // The check above would keep searching for that name and hide every other company.
    if (
      expandOptionsWhenSelected &&
      !multiple &&
      value &&
      safeLocalOptions.length > 0
    ) {
      const selectedOption = safeLocalOptions.find(
        (opt) => opt[valueKey] === value
      );
      const selectedLabel = selectedOption
        ? getCompanyLabel(selectedOption)
        : "";
      const displayingSelectedCompany =
        !!selectedLabel &&
        (inputValue === selectedLabel || debouncedInputValue === selectedLabel);

      if (displayingSelectedCompany) {
        nextFilters.name = undefined;
      }
    }

    return nextFilters;
  }, [
    debouncedInputValue,
    inputValue,
    activeCompaniesOnly,
    companyTypes,
    supplierId,
    rowsPerPage,
    multiple,
    value,
    deliveryTypeFilter,
    localOptions,
    valueKey,
    expandOptionsWhenSelected,
  ]);

  // Custom onChange handler that manages the internal state
  const handleChange = (selectedValue, selectedOption) => {
    setInternalUpdate(true);

    try {
      // Update local display options
      if (selectedOption) {
        if (multiple) {
          setInputValue(""); // reset search on select
          setLocalOptions(selectedOption);
        } else {
          setInputValue(getCompanyLabel(selectedOption));
          setLocalOptions([selectedOption]);
        }
      } else {
        setLocalOptions([]);
        setInputValue("");
      }

      // Pass to parent component
      if (props.onChange) {
        props.onChange(selectedValue, selectedOption);
      }
    } finally {
      // Reset the internal update flag after the cycle completes
      setTimeout(() => {
        setInternalUpdate(false);
      }, 0);
    }
  };

  // Custom onDataFetched that handles the initialization logic
  const handleDataFetched = (options) => {
    if (onDataFetched) {
      onDataFetched(options);
    }
  };

  // If a sidebar company filter exists and shouldn't be ignored, use it as the computed value
  let computedValue = value;
  if (sidebarCompanyFilter && !ignoreSideBarCompanyFilter) {
    computedValue =
      (multiple ? [sidebarCompanyFilter._id] : sidebarCompanyFilter._id) ||
      value;
  }

  // Convert react-hook-form error object to boolean for SearchSelect
  const hasError = !!error;

  // Extract error message if available
  const errorText = error?.message || "";

  // Create a SearchSelect props object and cast it to avoid type issues
  const searchSelectProps = {
    debouncedInputValue,
    inputValue,
    setInputValue,
    // When companyOptions is provided, always use it (for local-only mode)
    // Otherwise, use localOptions (for server-side search mode)
    localOptions: companyOptions || localOptions,
    setLocalOptions,
    value: computedValue,
    onChange: handleChange,
    valueKey,
    noOptionHint: "Aucune entreprise ne correspond",
    queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
    queryFn: CompanyServices.getCompanies,
    getOptionLabel: getCompanyLabel,
    queryfilters: filters,
    onDataFetched: handleDataFetched, // Use our custom handler
    label,
    placeholder,
    multiple,
    disabled,
    className,
    minCharsToTrigerSearch,
    labelKey: "name",
    labelKey2: "code",
    error: hasError,
    errorText,
    // When companyOptions is provided, disable server search and use only local options
    enabled: !companyOptions || companyOptions.length === 0,
    expandOptionsWhenSelected,
    ...props,
  } as any;

  return <SearchSelect {...searchSelectProps} />;
};

export default CompanySelect;
